// Уведомление в колокольчике шапки (data-model.md, kernel.notifications; ADR-0021).
import { z } from './zod.ts';
import { ErrorCodeSchema } from '../errors/catalog.ts';
import { IdSchema, OperationIdSchema, TimestampSchema } from './common.ts';

/**
 * Кнопка в уведомлении: подпись глаголом и внутренний переход (002, FR-053а: «Заказать снова»
 * у неудавшейся выгрузки ведёт в «Мои выгрузки» на этот заказ).
 */
export const NotificationActionSchema = z.object({
  label: z.string(),
  /** Внутренний переход, как link. */
  link: z.string(),
});

export type NotificationAction = z.infer<typeof NotificationActionSchema>;

/**
 * 032 (FR-001; plan.md, «Решения»): вкладки «Входящих» — «Все · Назначения · Сроки · Изменения».
 * Вкладку строки сервер выводит из вида уведомления (inboxTabOf).
 */
export const inboxTabs = ['all', 'assigned', 'due', 'changes'] as const;
export type InboxTab = (typeof inboxTabs)[number];
export const InboxTabSchema = z.enum(inboxTabs);

export const inboxTabLabels = {
  all: 'Все',
  assigned: 'Назначения',
  due: 'Сроки',
  changes: 'Изменения',
} as const satisfies Record<InboxTab, string>;

const ASSIGNED_KINDS: ReadonlySet<string> = new Set([
  'item.assigned',
  'item.assigned_many',
  'item.unassigned',
]);
const DUE_KINDS: ReadonlySet<string> = new Set([
  'item.due_changed',
  'item.due_soon',
  'item.overdue',
  'item.manager_copied',
]);
const CHANGE_KINDS: ReadonlySet<string> = new Set([
  'item.title_changed',
  'item.reopened',
  'item.archived',
  'item.restored',
  'item.answered',
  'answer.accepted',
  'answer.not_applied',
  'answer.contractor',
  'team.answered',
]);

/** Вкладка «Входящих» вида уведомления; прочие виды — только во «Все». */
export function inboxTabOf(kind: string): Exclude<InboxTab, 'all'> | null {
  if (ASSIGNED_KINDS.has(kind)) return 'assigned';
  if (DUE_KINDS.has(kind)) return 'due';
  if (CHANGE_KINDS.has(kind)) return 'changes';
  return null;
}

/** Виды вкладки — для фильтра ленты на сервере. */
export function inboxTabKinds(tab: Exclude<InboxTab, 'all'>): readonly string[] {
  return [...(tab === 'assigned' ? ASSIGNED_KINDS : tab === 'due' ? DUE_KINDS : CHANGE_KINDS)];
}

export const NotificationSchema = z.object({
  id: IdSchema,
  /** Код события из каталога событий: user.locked, substitution.created… */
  kind: z.string(),
  title: z.string(),
  body: z.string().optional(),
  /** Внутренний переход: /users/…, /profile, /substitutions, /support-access. */
  link: z.string().optional(),
  /**
   * Код ошибки и номер операции уведомления о сбое (002, FR-028, FR-053а): лента показывает их
   * строкой «Код … · операция …» с «Скопировать номер». Есть оба или ни одного.
   */
  code: ErrorCodeSchema.optional(),
  operation: OperationIdSchema.optional(),
  /** Кнопка действия под текстом (002, FR-053а). */
  action: NotificationActionSchema.optional(),
  /**
   * 031 (FR-004, FR-007; ADR-0046): уведомление о пункте — номера протокола и пункта, «требует
   * действия»; canOpen — протокол ещё виден пользователю (фильтр видимости при чтении): нет —
   * без перехода. У прочих уведомлений полей нет.
   */
  documentId: IdSchema.optional(),
  itemId: IdSchema.optional(),
  /**
   * «Требует действия»: у уведомлений о пунктах (031) и, с 032 (FR-005), у строк контролёру без
   * протокола — «Помощь», ответ не применён, ответное письмо, сбой письма. Нет поля — не требует.
   */
  actionRequired: z.boolean().optional(),
  canOpen: z.boolean().optional(),
  /** 032 (FR-001): вкладка «Входящих»; нет — только «Все». */
  tab: InboxTabSchema.exclude(['all']).optional(),
  /**
   * 0.26.1 (ACC-06): строка «требует действия» разобрана — entered (ответ внесён), closed
   * (закрыта без изменения пункта, отмечена разобранной), done (пункт выполнен, отменён или
   * человека сняли). Нет — не разобрана: строка в «Нерешённых», даже если прочитана.
   */
  resolvedAt: TimestampSchema.optional(),
  resolution: z.enum(['entered', 'closed', 'done']).optional(),
  createdAt: TimestampSchema,
  /** Для ролевых уведомлений — отметка текущего пользователя. */
  readAt: TimestampSchema.optional(),
});

export type Notification = z.infer<typeof NotificationSchema>;
